import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box, Button, Typography, Paper, IconButton, Menu, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  FormControl, InputLabel, Select, CircularProgress, Chip,
  Tooltip, Divider,
  FormControlLabel, Switch, alpha,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import BarChartIcon from '@mui/icons-material/BarChart';
import PieChartIcon from '@mui/icons-material/PieChart';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import TagIcon from '@mui/icons-material/Tag';
import TimelineIcon from '@mui/icons-material/Timeline';
import StackedBarChartIcon from '@mui/icons-material/StackedBarChart';
import SpeedIcon from '@mui/icons-material/Speed';
import DateRangeFilter from '../DateRangeFilter';
import { widgetBatchApi, ScorecardMiniRow } from '../../services/metricRegistryApi';
import AIInsightChip from '../ai/AIInsightChip';
import {
  InteractiveLineChart,
  InteractiveBarChart,
  InteractivePieChart,
  InteractiveDonutChart,
  NumberTrendWidget,
} from './chartsLazy'; // Perf 3g: recharts loads on first chart render
import WidgetDetailDialog from './WidgetDetailDialog';
import { HubThemeScope } from '../clients/hub/designSystem';
import { extractApiError } from '../../types/apiError';
import {
  dashboardsApi, departmentsApi, metricsApi, widgetDataApi,
} from '../../services/api';
import type {
  DashboardWidget, Department, Metric, MetricValue,
  WidgetType, WidgetSize, WidgetConfig,
} from '../../types';

const WIDGET_TYPE_OPTIONS: Array<{ value: WidgetType; label: string; icon: React.ReactNode; description: string }> = [
  { value: 'number_trend', label: 'Number + Trend', icon: <TagIcon />, description: 'Big number with trend comparison' },
  { value: 'line', label: 'Line Chart', icon: <ShowChartIcon />, description: 'Interactive time-series line graph' },
  { value: 'area', label: 'Area Chart', icon: <TimelineIcon />, description: 'Filled area chart with gradient' },
  { value: 'bar', label: 'Bar Chart', icon: <BarChartIcon />, description: 'Vertical or horizontal bars' },
  { value: 'pie', label: 'Pie Chart', icon: <PieChartIcon />, description: 'Proportional pie segments' },
  { value: 'donut', label: 'Donut Chart', icon: <DonutLargeIcon />, description: 'Ring chart with center value' },
  { value: 'count', label: 'Simple Count', icon: <StackedBarChartIcon />, description: 'Total or average value' },
  { value: 'scorecard_mini', label: 'Mini Scorecard', icon: <SpeedIcon />, description: 'One scorecard metric - pace, recovery and the week strip (current quarter)' },
];

const SIZE_OPTIONS: Array<{ value: WidgetSize; label: string; cols: number }> = [
  { value: 'third', label: '1/3 Width', cols: 1 },
  { value: 'quarter', label: '1/4 Width', cols: 1 },
  { value: 'half_horizontal', label: '1/2 Width', cols: 2 },
  { value: 'two_thirds', label: '2/3 Width', cols: 2 },
  { value: 'full', label: 'Full Width', cols: 3 },
];

// R4: picker preview swatches for 'default' updated to mirror the brand palette
// in ChartComponents.tsx (indigo / paprika / slate). cool/warm/monochrome unchanged.
const COLOR_THEMES = [
  { value: 'default', label: 'Default', colors: ['#0443ef', '#f76100', '#5B7C99'] },
  { value: 'cool', label: 'Cool', colors: ['#4FC3F7', '#4DD0E1', '#80CBC4'] },
  { value: 'warm', label: 'Warm', colors: ['#FF7043', '#FF8A65', '#FFE082'] },
  { value: 'monochrome', label: 'Mono', colors: ['#9E9E9E', '#757575', '#616161'] },
];

interface WidgetDataState {
  values: MetricValue[];
  total: number;
  average: number;
  denied?: boolean;
  scorecardMini?: ScorecardMiniRow;
  aggregation?: {
    currentValue: number;
    previousValue: number | null;
    changePercent: number | null;
    changeAbsolute: number | null;
    trend: 'up' | 'down' | 'flat';
    periodLabel: string;
    previousPeriodLabel: string | null;
    timeSeries: Array<{ date: string; value: number; label: string }>;
    breakdown: Array<{ label: string; value: number }>;
    metricUnit: string | null;
    metricName: string | null;
    isRateMetric: boolean;
  };
}

function safeParseConfig(config: string | null): WidgetConfig {
  if (!config) return {};
  try { return JSON.parse(config); } catch { return {}; }
}

interface WidgetBoardProps {
  dashboardId: number | null;
  dashboardMeta: { department_id: number | null; is_mixed: number } | null;
  onDashboardCreated?: (id: number) => void;
}

const WidgetBoard: React.FC<WidgetBoardProps> = ({ dashboardId, dashboardMeta, onDashboardCreated }) => {
  const [widgets, setWidgets] = useState<DashboardWidget[]>([]);
  const [widgetData, setWidgetData] = useState<Record<number, WidgetDataState>>({});
  const [metrics, setMetrics] = useState<Metric[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(false);

  const [effectiveDashboardId, setEffectiveDashboardId] = useState<number | null>(dashboardId);
  useEffect(() => { setEffectiveDashboardId(dashboardId); }, [dashboardId]);

  const ensureDashboard = async (): Promise<number> => {
    if (effectiveDashboardId) return effectiveDashboardId;
    const newDb = await dashboardsApi.create('Insights Board', { is_mixed: true });
    setEffectiveDashboardId(newDb.id);
    onDashboardCreated?.(newDb.id);
    return newDb.id;
  };

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingWidget, setEditingWidget] = useState<DashboardWidget | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuTarget, setMenuTarget] = useState<DashboardWidget | null>(null);

  // Detail drill-down
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailWidget, setDetailWidget] = useState<DashboardWidget | null>(null);

  // Form fields
  const [formTitle, setFormTitle] = useState('');
  const [formType, setFormType] = useState<WidgetType>('number_trend');
  const [formSize, setFormSize] = useState<WidgetSize>('third');
  const [formMetricId, setFormMetricId] = useState<number | ''>('');
  const [formDepartmentId, setFormDepartmentId] = useState<number | ''>('');
  const [formDateStart, setFormDateStart] = useState('');
  const [formDateEnd, setFormDateEnd] = useState('');
  const [formCalculation, setFormCalculation] = useState<'total' | 'average'>('total');
  const [formXAxisRange, setFormXAxisRange] = useState<'days' | 'weeks' | 'months' | 'years'>('weeks');
  const [formColorTheme, setFormColorTheme] = useState('default');
  const [formShowGoalLine, setFormShowGoalLine] = useState(false);
  const [formGoalValue, setFormGoalValue] = useState<number | ''>('');
  const [formShowLegend, setFormShowLegend] = useState(true);
  const [formBarOrientation, setFormBarOrientation] = useState<'vertical' | 'horizontal'>('vertical');
  const [formUnit, setFormUnit] = useState('');
  const [formPrefix, setFormPrefix] = useState('');
  const [formComparisonPeriod, setFormComparisonPeriod] = useState<'previous_period' | 'previous_year' | 'none'>('previous_period');

  useEffect(() => {
    Promise.all([metricsApi.getAll(), departmentsApi.getAll()]).then(([m, d]) => {
      setMetrics(m);
      setDepartments(d);
    }).catch((err) => console.error('Failed to load base data:', err));
  }, []);

  const loadWidgets = useCallback(async () => {
    if (!effectiveDashboardId) { setWidgets([]); return; }
    setLoading(true);
    try {
      const list = await dashboardsApi.listWidgets(effectiveDashboardId);
      setWidgets(list);
    } catch (err) {
      console.error('Failed to load widgets:', err);
      setWidgets([]);
    } finally {
      setLoading(false);
    }
  }, [effectiveDashboardId]);

  useEffect(() => { loadWidgets(); }, [loadWidgets]);

  useEffect(() => {
    // Scorecard rebuild P6 (AUD-036): ONE batched aggregate request for all
    // metric widgets (was 2 HTTP calls per widget) + ONE scorecard-mini
    // request for all mini-scorecard widgets. Denied metrics come back
    // flagged so the board renders the locked state instead of numbers.
    const loadData = async () => {
      const next: Record<number, WidgetDataState> = {};

      const miniWidgets: Array<{ id: number; metricId: number }> = [];
      const aggWidgets: Array<{ id: number; config: WidgetConfig }> = [];
      for (const widget of widgets) {
        const config: WidgetConfig = safeParseConfig(widget.config);
        if (!config.metricId) continue;
        if (widget.widget_type === 'scorecard_mini') miniWidgets.push({ id: widget.id, metricId: config.metricId });
        else aggWidgets.push({ id: widget.id, config });
      }

      const tasks: Promise<void>[] = [];

      if (aggWidgets.length > 0) {
        tasks.push(
          widgetBatchApi.batchAggregate(aggWidgets.map(w => ({
            metricId: w.config.metricId as number,
            dateStart: w.config.dateStart || undefined,
            dateEnd: w.config.dateEnd || undefined,
            bucketBy: w.config.xAxisRange || 'weeks',
            calculation: w.config.calculation || undefined,
            comparisonPeriod: w.config.comparisonPeriod || 'previous_period',
          }))).then(results => {
            // Allowed results align with the request order; denied entries
            // are appended by the backend - index allowed by position.
            const deniedIds = new Set(results.filter(r => r.denied).map(r => r.metricId));
            const allowed = results.filter(r => !r.denied);
            let cursor = 0;
            for (const w of aggWidgets) {
              if (deniedIds.has(w.config.metricId as number)) {
                next[w.id] = { values: [], total: 0, average: 0, denied: true };
                continue;
              }
              const agg = allowed[cursor++];
              if (!agg) continue;
              const value = agg.currentValue ?? 0;
              next[w.id] = {
                values: [],
                total: value,
                average: value,
                aggregation: {
                  currentValue: agg.currentValue ?? 0,
                  previousValue: agg.previousValue ?? null,
                  changePercent: agg.changePercent ?? null,
                  changeAbsolute: agg.changeAbsolute ?? null,
                  trend: agg.trend ?? 'flat',
                  periodLabel: agg.periodLabel ?? '',
                  previousPeriodLabel: agg.previousPeriodLabel ?? null,
                  timeSeries: agg.timeSeries ?? [],
                  breakdown: agg.breakdown ?? [],
                  metricUnit: agg.metricUnit ?? null,
                  metricName: agg.metricName ?? null,
                  isRateMetric: agg.isRateMetric ?? false,
                },
              };
            }
          }).catch(err => console.error('Batch aggregate failed:', err))
        );
      }

      if (miniWidgets.length > 0) {
        tasks.push(
          widgetBatchApi.scorecardMini(Array.from(new Set(miniWidgets.map(w => w.metricId)))).then(rows => {
            const byMetric = new Map(rows.map(r => [r.metric_id, r]));
            for (const w of miniWidgets) {
              const row = byMetric.get(w.metricId);
              if (!row) continue;
              next[w.id] = { values: [], total: 0, average: 0, denied: !!row.denied, scorecardMini: row };
            }
          }).catch(err => console.error('Scorecard-mini load failed:', err))
        );
      }

      await Promise.all(tasks);
      setWidgetData(next);
    };
    if (widgets.length > 0) loadData();
    else setWidgetData({});
  }, [widgets]);

  const openDialog = useCallback((widget?: DashboardWidget) => {
    metricsApi.getAll().then(setMetrics).catch((err) => console.error('WidgetBoard: failed to load metrics', err));
    if (widget) {
      setEditingWidget(widget);
      const config: WidgetConfig = safeParseConfig(widget.config);
      setFormTitle(widget.title);
      setFormType(widget.widget_type);
      setFormSize(widget.size);
      setFormMetricId(config.metricId ?? '');
      setFormDepartmentId(config.departmentId ?? '');
      setFormDateStart(config.dateStart ?? '');
      setFormDateEnd(config.dateEnd ?? '');
      setFormCalculation(config.calculation ?? 'total');
      setFormXAxisRange(config.xAxisRange ?? 'weeks');
      setFormColorTheme(config.colorTheme ?? 'default');
      setFormShowGoalLine(config.showGoalLine ?? false);
      setFormGoalValue(config.goalValue ?? '');
      setFormShowLegend(config.showLegend ?? true);
      setFormBarOrientation(config.barOrientation ?? 'vertical');
      setFormUnit(config.unit ?? '');
      setFormPrefix(config.prefix ?? '');
      setFormComparisonPeriod(config.comparisonPeriod ?? 'previous_period');
    } else {
      setEditingWidget(null);
      setFormTitle('');
      setFormType('number_trend');
      setFormSize('third');
      setFormMetricId('');
      setFormDepartmentId(dashboardMeta?.department_id ?? '');
      setFormDateStart('');
      setFormDateEnd('');
      setFormCalculation('total');
      setFormXAxisRange('weeks');
      setFormColorTheme('default');
      setFormShowGoalLine(false);
      setFormGoalValue('');
      setFormShowLegend(true);
      setFormBarOrientation('vertical');
      setFormUnit('');
      setFormPrefix('');
      setFormComparisonPeriod('previous_period');
    }
    setDialogOpen(true);
  }, [dashboardMeta]);

  const [saveError, setSaveError] = useState<string | null>(null);

  const handleSave = async () => {
    setSaveError(null);
    try {
      const dbId = await ensureDashboard();
      const config: WidgetConfig = {
        metricId: formMetricId === '' ? undefined : Number(formMetricId),
        departmentId: formDepartmentId === '' ? null : Number(formDepartmentId),
        dateStart: formDateStart || null,
        dateEnd: formDateEnd || null,
        calculation: formCalculation,
        xAxisRange: formXAxisRange,
        colorTheme: formColorTheme as any,
        showGoalLine: formShowGoalLine,
        goalValue: formGoalValue === '' ? undefined : Number(formGoalValue),
        showLegend: formShowLegend,
        barOrientation: formBarOrientation,
        unit: formUnit || undefined,
        prefix: formPrefix || undefined,
        comparisonPeriod: formComparisonPeriod,
      };
      const metricName = formMetricId ? metrics.find(m => m.id === Number(formMetricId))?.name : '';
      const payload = {
        title: formTitle.trim() || metricName || 'Widget',
        widget_type: formType,
        size: formSize,
        position: editingWidget?.position ?? widgets.length,
        config: JSON.stringify(config),
      };
      if (editingWidget) {
        await dashboardsApi.updateWidget(editingWidget.id, payload);
      } else {
        await dashboardsApi.createWidget(dbId, payload);
      }
      setDialogOpen(false);
      setEditingWidget(null);
      loadWidgets();
    } catch (err: any) {
      console.error('Failed to save widget:', err);
      setSaveError(extractApiError(err, 'Failed to save widget. Please try again.'));
    }
  };

  const handleDelete = async (widgetId: number) => {
    try {
      await dashboardsApi.deleteWidget(widgetId);
      loadWidgets();
    } catch (err) {
      console.error('Failed to delete widget:', err);
    }
  };

  const openDetail = (widget: DashboardWidget) => {
    setDetailWidget(widget);
    setDetailOpen(true);
  };

  const getGridSpan = (size: WidgetSize): number => {
    switch (size) {
      case 'full': return 3;
      case 'two_thirds': case 'half_horizontal': return 2;
      case 'half_vertical': return 1;
      default: return 1;
    }
  };

  const getMinHeight = (type: WidgetType, size: WidgetSize): number => {
    if (type === 'number_trend' || type === 'count') return 160;
    if (type === 'scorecard_mini') return 200;
    if (size === 'full' || size === 'two_thirds') return 320;
    return 260;
  };

  const renderWidget = (widget: DashboardWidget) => {
    const config: WidgetConfig = safeParseConfig(widget.config);
    const data = widgetData[widget.id];
    const agg = data?.aggregation;

    if (!config.metricId) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, opacity: 0.5 }}>
          <Typography variant="body2">Click settings to configure this widget</Typography>
        </Box>
      );
    }
    if (!data) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (data.denied) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', flex: 1, gap: 0.5 }}>
          <Typography variant="subtitle2" sx={{ color: 'text.disabled', fontWeight: 700 }}>Restricted metric</Typography>
          <Typography variant="caption" color="text.secondary">
            This metric is deny-listed for your role - the API withholds its numbers.
          </Typography>
        </Box>
      );
    }

    const isRate = agg?.isRateMetric || false;
    const unit = config.unit || (isRate && !config.unit ? '%' : '');
    const prefix = config.prefix || '';
    const calcLabel = isRate || config.calculation === 'average' ? 'Average for period' : 'Total for period';

    switch (widget.widget_type) {
      case 'scorecard_mini':
        return <MiniScorecardCard row={data.scorecardMini} />;

      case 'number_trend':
        return (
          <NumberTrendWidget
            value={agg?.currentValue ?? data.total}
            previousValue={agg?.previousValue}
            changePercent={agg?.changePercent}
            changeAbsolute={agg?.changeAbsolute}
            trend={agg?.trend}
            label={calcLabel}
            periodLabel={agg?.periodLabel}
            unit={unit}
            prefix={prefix}
            sparklineData={agg?.timeSeries?.map(p => ({ value: p.value }))}
            colorTheme={config.colorTheme}
          />
        );

      case 'line':
      case 'area':
        return (
          <InteractiveLineChart
            data={agg?.timeSeries || []}
            height={getMinHeight(widget.widget_type, widget.size) - 60}
            showArea={widget.widget_type === 'area' || widget.widget_type === 'line'}
            showPoints
            unit={unit}
            prefix={prefix}
            colorTheme={config.colorTheme}
            goalValue={config.goalValue}
            showGoalLine={config.showGoalLine}
            isRateMetric={isRate}
          />
        );

      case 'bar':
        return (
          <InteractiveBarChart
            data={(agg?.timeSeries || []).map(p => ({ label: p.label, value: p.value }))}
            height={getMinHeight(widget.widget_type, widget.size) - 60}
            unit={unit}
            prefix={prefix}
            colorTheme={config.colorTheme}
            orientation={config.barOrientation}
            isRateMetric={isRate}
          />
        );

      case 'pie':
        return (
          <InteractivePieChart
            data={agg?.breakdown || []}
            height={getMinHeight(widget.widget_type, widget.size) - 40}
            unit={unit}
            prefix={prefix}
            colorTheme={config.colorTheme}
            showLegend={config.showLegend}
          />
        );

      case 'donut':
        return (
          <InteractiveDonutChart
            data={agg?.breakdown || []}
            height={getMinHeight(widget.widget_type, widget.size) - 40}
            unit={unit}
            prefix={prefix}
            colorTheme={config.colorTheme}
            centerValue={`${prefix}${(agg?.currentValue ?? data.total).toLocaleString()}`}
            centerLabel="Total"
          />
        );

      case 'count':
      default: {
        const value = config.calculation === 'average' ? data.average : data.total;
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, justifyContent: 'center', flex: 1 }}>
            <Typography variant="h4" fontWeight={700}>
              {prefix}{value.toLocaleString(undefined, { maximumFractionDigits: 2 })}{unit}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {config.calculation === 'average' ? 'Average' : 'Total'} for selected range
            </Typography>
            {agg && agg.changePercent != null && (
              <Chip
                size="small"
                icon={agg.trend === 'up' ? <TrendingUpIcon /> : agg.trend === 'down' ? <TrendingDownIcon /> : <TrendingFlatIcon />}
                label={`${agg.changePercent > 0 ? '+' : ''}${agg.changePercent.toFixed(1)}% vs prev period`}
                sx={{
                  alignSelf: 'flex-start',
                  bgcolor: agg.trend === 'up' ? 'var(--status-on-track-soft)' : agg.trend === 'down' ? 'var(--status-off-track-soft)' : 'action.hover',
                  color: agg.trend === 'up' ? 'var(--status-on-track-on-soft)' : agg.trend === 'down' ? 'var(--status-off-track-on-soft)' : 'text.secondary',
                  fontWeight: 600,
                }}
              />
            )}
          </Box>
        );
      }
    }
  };

  const filteredMetrics = useMemo(() => {
    if (dashboardMeta?.is_mixed === 0 && dashboardMeta?.department_id) {
      return metrics.filter(m => m.department_id === dashboardMeta.department_id);
    }
    if (formDepartmentId) {
      return metrics.filter(m => m.department_id === Number(formDepartmentId));
    }
    return metrics;
  }, [metrics, dashboardMeta, formDepartmentId]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <AIInsightChip context="dashboard" />
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>Widget Board</Typography>
          <Typography variant="caption" color="text.secondary">
            {widgets.length} widget{widgets.length !== 1 ? 's' : ''} configured
          </Typography>
        </Box>
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => openDialog()}>
          Add Widget
        </Button>
      </Box>

      {/* Widget Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : widgets.length === 0 ? (
        <Paper sx={{
          minHeight: 400, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 2,
          border: '2px dashed', borderColor: 'divider', borderRadius: 3,
        }}>
          <Box sx={{ fontSize: 44, opacity: 0.3 }}>📊</Box>
          <Typography variant="h6" color="text.secondary">No widgets yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, textAlign: 'center' }}>
            Add widgets to build your custom dashboard. Choose from line charts, pie charts, donut charts, bar charts, and trend numbers.
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => openDialog()}>
            Add Your First Widget
          </Button>
        </Paper>
      ) : (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 2.5,
          '@media (min-width: 1600px)': { gridTemplateColumns: 'repeat(4, 1fr)' },
          '@media (max-width: 900px)': { gridTemplateColumns: 'repeat(2, 1fr)' },
          '@media (max-width: 600px)': { gridTemplateColumns: '1fr' },
        }}>
          {widgets.map((widget) => {
            const span = getGridSpan(widget.size);
            const minH = getMinHeight(widget.widget_type, widget.size);
            return (
              <Paper
                key={widget.id}
                elevation={0}
                sx={{
                  gridColumn: `span ${span}`,
                  minHeight: minH,
                  p: 2.5,
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: (theme) => `0 0 0 1px ${alpha(theme.palette.primary.main, 0.3)}`,
                    '& .widget-actions': { opacity: 1 },
                  },
                }}
                onClick={() => openDetail(widget)}
              >
                {/* Widget header */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" fontWeight={600} noWrap>{widget.title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {WIDGET_TYPE_OPTIONS.find(t => t.value === widget.widget_type)?.label || widget.widget_type}
                    </Typography>
                  </Box>
                  <Box className="widget-actions" sx={{ opacity: 0, transition: 'opacity 0.15s', display: 'flex', gap: 0.5 }}
                    onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Expand">
                      <IconButton size="small" onClick={() => openDetail(widget)}>
                        <OpenInFullIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Settings">
                      <IconButton size="small" onClick={() => openDialog(widget)}>
                        <SettingsIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <IconButton size="small" onClick={(e) => { setMenuAnchor(e.currentTarget); setMenuTarget(widget); }}>
                      <MoreVertIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                </Box>
                {/* Widget body */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  {renderWidget(widget)}
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}

      {/* Context menu */}
      <HubThemeScope>
        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => { setMenuAnchor(null); setMenuTarget(null); }}>
          <MenuItem onClick={() => { if (menuTarget) openDialog(menuTarget); setMenuAnchor(null); setMenuTarget(null); }}>
            <SettingsIcon fontSize="small" sx={{ mr: 1 }} /> Edit Widget
          </MenuItem>
          <MenuItem onClick={() => { if (menuTarget) openDetail(menuTarget); setMenuAnchor(null); setMenuTarget(null); }}>
            <OpenInFullIcon fontSize="small" sx={{ mr: 1 }} /> View Details
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => { if (menuTarget) handleDelete(menuTarget.id); setMenuAnchor(null); setMenuTarget(null); }} sx={{ color: 'var(--status-off-track)' }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Remove Widget
          </MenuItem>
        </Menu>
      </HubThemeScope>

      {/* Detail drill-down */}
      {detailWidget && (
        <WidgetDetailDialog
          open={detailOpen}
          onClose={() => { setDetailOpen(false); setDetailWidget(null); }}
          title={detailWidget.title}
          metricId={safeParseConfig(detailWidget.config).metricId}
          widgetType={detailWidget.widget_type}
          config={safeParseConfig(detailWidget.config)}
        />
      )}

      {/* Add/Edit Widget Dialog */}
      <HubThemeScope>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingWidget ? 'Edit Widget' : 'Add Widget'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '16px !important' }}>
          {/* Widget type selector */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Widget Type</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 1 }}>
              {WIDGET_TYPE_OPTIONS.map((opt) => (
                <Paper
                  key={opt.value}
                  elevation={0}
                  onClick={() => setFormType(opt.value)}
                  sx={{
                    p: 1.5, cursor: 'pointer', textAlign: 'center',
                    border: '2px solid',
                    borderColor: formType === opt.value ? 'primary.main' : 'divider',
                    bgcolor: formType === opt.value ? (theme) => alpha(theme.palette.primary.main, 0.08) : 'transparent',
                    borderRadius: '6px',
                    transition: 'all 0.15s',
                    '&:hover': { borderColor: 'primary.main' },
                  }}
                >
                  <Box sx={{ color: formType === opt.value ? 'primary.main' : 'text.secondary', mb: 0.5 }}>
                    {opt.icon}
                  </Box>
                  <Typography variant="caption" fontWeight={formType === opt.value ? 700 : 400}>
                    {opt.label}
                  </Typography>
                </Paper>
              ))}
            </Box>
          </Box>

          <TextField
            label="Widget Title"
            value={formTitle}
            onChange={(e) => setFormTitle(e.target.value)}
            placeholder="Auto-generated from metric name if left empty"
            size="small"
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>Size</InputLabel>
              <Select label="Size" value={formSize} onChange={(e) => setFormSize(e.target.value as WidgetSize)}>
                {SIZE_OPTIONS.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>Calculation</InputLabel>
              <Select label="Calculation" value={formCalculation} onChange={(e) => setFormCalculation(e.target.value as any)}>
                <MenuItem value="total">Total</MenuItem>
                <MenuItem value="average">Average</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {dashboardMeta?.is_mixed !== 0 && (
            <FormControl size="small" fullWidth>
              <InputLabel>Department Filter</InputLabel>
              <Select label="Department Filter" value={formDepartmentId} onChange={(e) => setFormDepartmentId(e.target.value as any)}>
                <MenuItem value="">All Departments</MenuItem>
                {departments.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
              </Select>
            </FormControl>
          )}

          <FormControl size="small" fullWidth>
            <InputLabel>Metric</InputLabel>
            <Select label="Metric" value={formMetricId} onChange={(e) => {
              const id = e.target.value as any;
              setFormMetricId(id);
              if (id) {
                const m = metrics.find(met => met.id === Number(id));
                if (m?.unit) {
                  const lower = m.unit.toLowerCase().trim();
                  const isRate = ['%', 'percent', 'ratio', 'rate', 'score', 'rating', 'avg', 'average'].includes(lower);
                  if (isRate) {
                    setFormCalculation('average');
                    if (!formUnit) setFormUnit(m.unit);
                  } else if (!formUnit) {
                    setFormUnit(m.unit);
                  }
                  if (lower === '$' || lower === 'usd' || lower === 'dollars') {
                    if (!formPrefix) setFormPrefix('$');
                    setFormUnit('');
                  }
                }
              }
            }}>
              <MenuItem value="">Select a metric</MenuItem>
              {filteredMetrics.map(m => (
                <MenuItem key={m.id} value={m.id}>
                  {m.name}{m.unit ? ` (${m.unit})` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Chart-specific options */}
          {(formType === 'line' || formType === 'area' || formType === 'bar') && (
            <>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl size="small" sx={{ flex: 1 }}>
                  <InputLabel>Time Range</InputLabel>
                  <Select label="Time Range" value={formXAxisRange} onChange={(e) => setFormXAxisRange(e.target.value as any)}>
                    <MenuItem value="days">Days</MenuItem>
                    <MenuItem value="weeks">Weeks</MenuItem>
                    <MenuItem value="months">Months</MenuItem>
                    <MenuItem value="years">Years</MenuItem>
                  </Select>
                </FormControl>
                {formType === 'bar' && (
                  <FormControl size="small" sx={{ flex: 1 }}>
                    <InputLabel>Orientation</InputLabel>
                    <Select label="Orientation" value={formBarOrientation} onChange={(e) => setFormBarOrientation(e.target.value as any)}>
                      <MenuItem value="vertical">Vertical</MenuItem>
                      <MenuItem value="horizontal">Horizontal</MenuItem>
                    </Select>
                  </FormControl>
                )}
              </Box>
            </>
          )}

          {/* Comparison period */}
          {(formType === 'number_trend' || formType === 'count') && (
            <FormControl size="small" fullWidth>
              <InputLabel>Compare Against</InputLabel>
              <Select label="Compare Against" value={formComparisonPeriod} onChange={(e) => setFormComparisonPeriod(e.target.value as any)}>
                <MenuItem value="previous_period">Previous Period</MenuItem>
                <MenuItem value="previous_year">Previous Year</MenuItem>
                <MenuItem value="none">No Comparison</MenuItem>
              </Select>
            </FormControl>
          )}

          {/* Value formatting */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="Prefix (e.g. $)" value={formPrefix} onChange={(e) => setFormPrefix(e.target.value)} size="small" sx={{ flex: 1 }} />
            <TextField label="Unit (e.g. hrs, %)" value={formUnit} onChange={(e) => setFormUnit(e.target.value)} size="small" sx={{ flex: 1 }} />
          </Box>

          {/* Color theme */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Color Theme</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {COLOR_THEMES.map(ct => (
                <Paper
                  key={ct.value}
                  elevation={0}
                  onClick={() => setFormColorTheme(ct.value)}
                  sx={{
                    p: 1, cursor: 'pointer', flex: 1, textAlign: 'center',
                    border: '2px solid',
                    borderColor: formColorTheme === ct.value ? 'primary.main' : 'divider',
                    borderRadius: 1.5,
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', mb: 0.5 }}>
                    {ct.colors.map((c, i) => (
                      <Box key={i} sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: c }} />
                    ))}
                  </Box>
                  <Typography variant="caption">{ct.label}</Typography>
                </Paper>
              ))}
            </Box>
          </Box>

          {/* Goal line */}
          {(formType === 'line' || formType === 'area') && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <FormControlLabel
                control={<Switch checked={formShowGoalLine} onChange={(e) => setFormShowGoalLine(e.target.checked)} size="small" />}
                label="Show Goal Line"
              />
              {formShowGoalLine && (
                <TextField
                  label="Goal Value"
                  type="number"
                  value={formGoalValue}
                  onChange={(e) => setFormGoalValue(e.target.value === '' ? '' : Number(e.target.value))}
                  size="small"
                  sx={{ width: 150 }}
                />
              )}
            </Box>
          )}

          {/* Date filter */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Date Filter</Typography>
            <DateRangeFilter
              onFilterChange={(start, end) => { setFormDateStart(start || ''); setFormDateEnd(end || ''); }}
              currentStartDate={formDateStart || null}
              currentEndDate={formDateEnd || null}
            />
          </Box>
        </DialogContent>
        {saveError && (
          <Box sx={{ px: 3, pb: 1 }}>
            <Typography variant="body2" sx={{ color: 'var(--status-off-track)' }}>{saveError}</Typography>
          </Box>
        )}
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setDialogOpen(false); setSaveError(null); }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            {editingWidget ? 'Update Widget' : 'Add Widget'}
          </Button>
        </DialogActions>
      </Dialog>
      </HubThemeScope>
    </Box>
  );
};

// ── Mini Scorecard card (scorecard rebuild P6) ─────────────────────────
//
// The graduated prototype-D widget on live data: one registry metric,
// compact — ACT vs TRG to date, the pace chip with recovery math, and the
// week strip. Status colors route through the AUD-112 theme tokens;
// the locked/denied state renders in the parent before this is reached.

function fmtMini(v: number, unit: string | null | undefined): string {
  const abs = Math.abs(v);
  const body = unit === '$'
    ? `$${abs >= 1000 ? `${(abs / 1000).toFixed(abs >= 10000 ? 0 : 1)}k` : abs.toFixed(0)}`
    : abs >= 100 ? abs.toFixed(0) : abs % 1 === 0 ? abs.toFixed(0) : abs.toFixed(abs < 1 ? 2 : 1);
  const sign = v < 0 ? '−' : '';
  return unit === '%' ? `${sign}${body}%` : `${sign}${body}`;
}

function MiniScorecardCard({ row }: { row?: ScorecardMiniRow }) {
  if (!row || row.missing) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, opacity: 0.6 }}>
        <Typography variant="body2">Metric not found — reconfigure this widget.</Typography>
      </Box>
    );
  }
  const weeks = row.weeks ?? [];
  const pace = row.pace;
  const asOf = pace?.as_of_week ?? '';
  const elapsed = weeks.filter(w => w.week_start <= asOf);
  const targetToDate = elapsed.reduce((sum, w) => sum + w.weekly_target, 0);
  const isAvgKind = row.target_type === 'threshold' || (row.unit || '').includes('%');
  const actual = row.quarter_actual ?? 0;
  const recovery = pace && !pace.on_pace && pace.mode === 'cumulative_total' && pace.required_per_week_to_recover != null && pace.weeks_remaining > 0
    ? `need ${fmtMini(pace.required_per_week_to_recover, row.unit)}/wk to recover`
    : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, justifyContent: 'center' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        {row.classification && (
          <Box component="span" sx={{
            px: 0.625, py: 0.125, borderRadius: '4px', border: '1px solid var(--status-neutral)',
            color: 'var(--status-neutral-on-soft)', fontSize: 10.5, fontWeight: 800,
            letterSpacing: '0.09em', textTransform: 'uppercase', lineHeight: 1.5,
          }}>
            {row.classification}
          </Box>
        )}
        <Typography variant="caption" color="text.secondary">Q{row.quarter} {row.year}</Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
        <Typography sx={{ fontFamily: '"Inter Tight", "Inter", sans-serif', fontSize: 30, fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1 }}>
          {fmtMini(actual, row.unit)}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          {row.quarter_target != null
            ? `vs ${fmtMini(row.target_type === 'total' ? targetToDate : row.quarter_target, row.unit)} ${row.target_type === 'total' ? 'to date' : isAvgKind ? 'avg/wk' : '/wk'}`
            : 'no target set'}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minHeight: 22, flexWrap: 'wrap' }}>
        {pace && row.quarter_target != null && (
          <Box component="span" sx={{
            px: 0.875, py: 0.25, borderRadius: '4px',
            bgcolor: pace.on_pace ? 'var(--status-on-track-soft)' : 'var(--status-off-track-soft)',
            color: pace.on_pace ? 'var(--status-on-track-on-soft)' : 'var(--status-off-track-on-soft)',
            fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
          }}>
            {pace.delta >= 0 ? '▲' : '▼'} {fmtMini(pace.delta, row.unit)}
          </Box>
        )}
        {recovery && (
          <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: 'var(--status-off-track-on-soft)' }}>
            {recovery}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: '3px' }}>
        {weeks.map((w) => {
          const isElapsed = w.week_start <= asOf;
          return (
            <Box
              key={w.week_start}
              title={`${w.week_start}: ${fmtMini(w.weekly_actual, row.unit)}`}
              sx={{
                flex: 1, height: 6, borderRadius: '3px',
                bgcolor: !isElapsed
                  ? 'var(--rule-soft)'
                  : row.quarter_target == null
                    ? 'var(--status-neutral)'
                    : w.is_on_track ? 'var(--status-on-track)' : 'var(--status-off-track)',
                outline: w.week_start === asOf ? '1.5px solid var(--indigo)' : 'none',
                outlineOffset: '1px',
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
}

export default WidgetBoard;
